import React, { useCallback, useEffect, useId, useRef, useState } from "react";

import { apiFetch } from "../services/api";



let mermaidModule = null;



async function loadMermaid() {

  if (mermaidModule) return mermaidModule;

  const mermaid = (await import("mermaid")).default;

  mermaid.initialize({

    startOnLoad: false,

    theme: "dark",

    securityLevel: "loose",

    fontSize: 16,

    flowchart: {

      nodeSpacing: 36,

      rankSpacing: 48,

      // SVG text scales crisply on zoom; htmlLabels (foreignObject) get blurry.

      htmlLabels: false,

      useMaxWidth: false,

    },

  });

  mermaidModule = mermaid;

  return mermaid;

}



function readMermaidNaturalSize(el) {
  if (!el) return { w: 0, h: 0 };
  const vb = el.viewBox && el.viewBox.baseVal;
  let w = vb && vb.width ? vb.width : 0;
  let h = vb && vb.height ? vb.height : 0;
  if (!w || !h) {
    try {
      const box = el.getBBox();
      w = box.width || w;
      h = box.height || h;
    } catch (_) {
      /* ignore */
    }
  }
  if (!w || !h) {
    w = Number.parseFloat(el.getAttribute("width")) || 800;
    h = Number.parseFloat(el.getAttribute("height")) || 600;
  }
  return { w, h };
}

/**
 * Size the SVG in CSS pixels while keeping the original viewBox.
 * That scales vector shapes/text crisply (no CSS transform blur).
 * Mermaid often ships width="100%" — strip that or zoom only grows height.
 */
function applySvgZoom(svg, base, zoomLevel) {
  if (!svg || !base?.width || !base?.height) return;
  const z = Math.max(0.25, zoomLevel || 1);
  const w = Math.max(1, Math.round(base.width * z));
  const h = Math.max(1, Math.round(base.height * z));

  // Preserve natural viewBox so content scales with width/height.
  if (!svg.getAttribute("viewBox") && base.natW && base.natH) {
    svg.setAttribute("viewBox", `0 0 ${base.natW} ${base.natH}`);
  }

  svg.removeAttribute("style");
  svg.setAttribute("width", String(w));
  svg.setAttribute("height", String(h));
  svg.style.cssText = [
    `width:${w}px`,
    `height:${h}px`,
    "max-width:none",
    "max-height:none",
    "display:block",
  ].join(";");
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
}

function applyStageZoom(stageEl, base, zoomLevel) {
  if (!stageEl || !base?.width || !base?.height) return;
  const z = Math.max(0.25, zoomLevel || 1);
  // Drop any leftover transform scaler from older zoom impl.
  const scaler = stageEl.querySelector(".artifact-diagram-scaler");
  if (scaler) {
    while (scaler.firstChild) stageEl.insertBefore(scaler.firstChild, scaler);
    scaler.remove();
  }
  const svg = stageEl.querySelector("svg");
  applySvgZoom(svg, base, z);
  stageEl.style.width = `${Math.round(base.width * z)}px`;
  stageEl.style.height = `${Math.round(base.height * z)}px`;
  stageEl.style.transform = "";
  stageEl.style.overflow = "visible";
}

function computeInlineBaseSize(el, measureEl) {
  const { w, h } = readMermaidNaturalSize(el);
  if (!w || !h || !measureEl) return null;
  const available = Math.max(160, measureEl.clientWidth - 32);
  const width = available;
  const height = Math.max(120, (h / w) * available);
  return { width, height, natW: w, natH: h };
}

function computeFullscreenBaseSize(el, root) {
  const { w, h } = readMermaidNaturalSize(el);
  if (!w || !h || !root) return null;
  const header = root.querySelector(".artifact-diagram-fullscreen-header");
  const availW = Math.max(160, root.clientWidth - 24);
  const availH = Math.max(
    120,
    root.clientHeight - (header ? header.offsetHeight : 0) - 24
  );
  const scale = Math.min(availW / w, availH / h);
  return {
    width: w * scale,
    height: h * scale,
    natW: w,
    natH: h,
  };
}

export function MermaidPreview({ source, diagramKey }) {
  const scrollRef = useRef(null);
  const stageRef = useRef(null);
  const fullscreenBodyRef = useRef(null);
  const fullscreenStageRef = useRef(null);
  const fullscreenRootRef = useRef(null);
  const inlineBaseRef = useRef({ width: 0, height: 0 });
  const fsBaseRef = useRef({ width: 0, height: 0 });
  const zoomRef = useRef(1);
  const fsZoomRef = useRef(1);
  const [error, setError] = useState("");
  const [fullscreen, setFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [fsZoom, setFsZoom] = useState(1);
  zoomRef.current = zoom;
  fsZoomRef.current = fsZoom;

  const paintInline = useCallback((zoomLevel) => {
    applyStageZoom(stageRef.current, inlineBaseRef.current, zoomLevel);
  }, []);

  const paintFullscreen = useCallback((zoomLevel) => {
    applyStageZoom(fullscreenStageRef.current, fsBaseRef.current, zoomLevel);
  }, []);

  const recomputeInlineBase = useCallback(() => {
    const stage = stageRef.current;
    const scroll = scrollRef.current;
    const svg = stage?.querySelector("svg");
    if (!svg || !scroll) return;
    const base = computeInlineBaseSize(svg, scroll);
    if (base) inlineBaseRef.current = base;
  }, []);

  const recomputeFullscreenBase = useCallback(() => {
    const stage = fullscreenStageRef.current;
    const root = fullscreenRootRef.current;
    const svg = stage?.querySelector("svg");
    if (!svg || !root) return;
    const base = computeFullscreenBaseSize(svg, root);
    if (base) fsBaseRef.current = base;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!source || !stageRef.current) return;
      setError("");
      try {
        const mermaid = await loadMermaid();
        const id = `mermaid-${diagramKey}`.replace(/[^a-zA-Z0-9_-]/g, "_");
        const { svg } = await mermaid.render(id, source);
        if (!cancelled && stageRef.current) {
          stageRef.current.innerHTML = svg;
          inlineBaseRef.current = { width: 0, height: 0 };
          recomputeInlineBase();
          paintInline(zoomRef.current);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Could not render diagram");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source, diagramKey, recomputeInlineBase, paintInline]);

  useEffect(() => {
    paintInline(zoom);
  }, [zoom, paintInline]);

  useEffect(() => {
    const onResize = () => {
      recomputeInlineBase();
      paintInline(zoomRef.current);
      if (fullscreenRootRef.current) {
        recomputeFullscreenBase();
        paintFullscreen(fsZoomRef.current);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [recomputeInlineBase, recomputeFullscreenBase, paintInline, paintFullscreen]);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const stage = fullscreenStageRef.current;
    const inlineStage = stageRef.current;
    if (!stage || !inlineStage) return undefined;

    // Copy raw SVG only (not the scaled wrapper) so fullscreen can re-fit.
    const svg = inlineStage.querySelector("svg");
    stage.innerHTML = svg ? svg.outerHTML : inlineStage.innerHTML;
    fsBaseRef.current = { width: 0, height: 0 };
    const raf = window.requestAnimationFrame(() => {
      recomputeFullscreenBase();
      setFsZoom(1);
      paintFullscreen(1);
    });

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.cancelAnimationFrame(raf);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [fullscreen, source, diagramKey, recomputeFullscreenBase, paintFullscreen]);

  useEffect(() => {
    if (!fullscreen) return;
    paintFullscreen(fsZoom);
  }, [fullscreen, fsZoom, paintFullscreen]);

  if (error) {
    return <pre className="artifact-diagram-error">{error}</pre>;
  }

  return (
    <div className="artifact-diagram-wrap">
      <div className="artifact-diagram-toolbar">
        <button
          type="button"
          onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}
        >
          Zoom in
        </button>
        <button
          type="button"
          onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
        >
          Zoom out
        </button>
        <button type="button" onClick={() => setZoom(1)}>
          Reset
        </button>
        <button
          type="button"
          onClick={() => {
            setFsZoom(1);
            setFullscreen(true);
          }}
        >
          Fullscreen
        </button>
      </div>
      <div
        className={
          zoom !== 1
            ? "artifact-diagram-preview is-zoomed"
            : "artifact-diagram-preview"
        }
        ref={scrollRef}
      >
        <div className="artifact-diagram-stage" ref={stageRef} />
      </div>
      {fullscreen && (
        <div
          className="artifact-diagram-fullscreen"
          role="dialog"
          aria-modal="true"
          aria-label="Diagram fullscreen"
          ref={fullscreenRootRef}
        >
          <div className="artifact-diagram-fullscreen-header">
            <span>Diagram</span>
            <div className="artifact-diagram-fullscreen-actions">
              <button
                type="button"
                onClick={() =>
                  setFsZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))
                }
              >
                Zoom in
              </button>
              <button
                type="button"
                onClick={() =>
                  setFsZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))
                }
              >
                Zoom out
              </button>
              <button
                type="button"
                onClick={() => {
                  recomputeFullscreenBase();
                  paintFullscreen(1);
                  setFsZoom(1);
                }}
              >
                Fit
              </button>
              <button type="button" onClick={() => setFullscreen(false)}>
                Close
              </button>
            </div>
          </div>
          <div
            className={
              fsZoom > 1
                ? "artifact-diagram-fullscreen-body is-zoomed"
                : "artifact-diagram-fullscreen-body"
            }
            ref={fullscreenBodyRef}
          >
            <div className="artifact-diagram-stage" ref={fullscreenStageRef} />
          </div>
        </div>
      )}
    </div>
  );
}



async function downloadArtifact(artifact) {

  const url = artifact.download_url;

  if (!url) {

    throw new Error("No download URL for this artifact");

  }

  const response = await apiFetch(url, { method: "GET" });

  if (!response.ok) {

    throw new Error("Download failed");

  }

  const blob = await response.blob();

  const objectUrl = window.URL.createObjectURL(blob);

  const link = document.createElement("a");

  link.href = objectUrl;

  link.download = artifact.filename || "download";

  document.body.appendChild(link);

  link.click();

  link.remove();

  window.URL.revokeObjectURL(objectUrl);

}



async function fetchArtifactMeta(artifact) {

  const id = artifact?.artifact_id;

  if (!id) return artifact;

  const response = await apiFetch(`/api/agent/artifacts/${id}/meta`, {

    method: "GET",

  });

  if (!response.ok) {

    throw new Error("Could not load artifact metadata");

  }

  return response.json();

}



function formatLabel(artifact) {

  const title =

    artifact.title ||

    artifact.entity_name ||

    artifact.filename ||

    "Generated artifact";

  const fmt = String(artifact.format || "").toUpperCase();

  return fmt ? `${title} (${fmt})` : title;

}



function ArtifactCard({ artifact, cardKey }) {

  const [resolved, setResolved] = useState(artifact);

  const [downloading, setDownloading] = useState(false);

  const [error, setError] = useState(null);



  useEffect(() => {

    let cancelled = false;

    (async () => {

      const isDiagram =

        artifact.type === "diagram" || artifact.format === "mermaid";

      if (!isDiagram || artifact.source || !artifact.artifact_id) {

        setResolved(artifact);

        return;

      }

      try {

        const meta = await fetchArtifactMeta(artifact);

        if (!cancelled) {

          setResolved({ ...artifact, ...meta });

        }

      } catch (e) {

        if (!cancelled) {

          setResolved(artifact);

          setError(e.message || "Could not load diagram preview");

        }

      }

    })();

    return () => {

      cancelled = true;

    };

  }, [artifact]);



  const isDiagram =

    resolved.type === "diagram" || resolved.format === "mermaid";

  const downloadUrl = resolved.download_url;



  const handleDownload = async () => {

    setError(null);

    setDownloading(true);

    try {

      let target = resolved;

      if (!target.download_url && target.artifact_id) {

        target = await fetchArtifactMeta(target);

        setResolved((prev) => ({ ...prev, ...target }));

      }

      await downloadArtifact(target);

    } catch (e) {

      setError(e.message || "Download failed");

    } finally {

      setDownloading(false);

    }

  };



  return (

    <div className="agent-artifact-card">

      <div className="agent-artifact-header">

        <span className="agent-artifact-label">{formatLabel(resolved)}</span>

        {(downloadUrl || resolved.artifact_id) && (

          <button

            type="button"

            className="agent-artifact-download-btn"

            disabled={downloading}

            onClick={handleDownload}

          >

            {downloading

              ? "Downloading…"

              : `Download ${String(resolved.format || "file").toUpperCase()}`}

          </button>

        )}

      </div>

      {error && <div className="agent-artifacts-error">{error}</div>}

      {isDiagram && resolved.source && (

        <MermaidPreview source={resolved.source} diagramKey={cardKey} />

      )}

      {!isDiagram && resolved.preview_text && (

        <pre className="artifact-preview-text">{resolved.preview_text}</pre>

      )}

    </div>

  );

}



const AgentArtifacts = ({ artifacts = [] }) => {

  const baseId = useId();



  if (!Array.isArray(artifacts) || artifacts.length === 0) {

    return null;

  }



  return (

    <div className="agent-artifacts-section">

      <div className="agent-artifacts-title">Deliverables</div>

      {artifacts.map((artifact, idx) => {

        const key = artifact.artifact_id || `${baseId}-${idx}`;

        return <ArtifactCard key={key} artifact={artifact} cardKey={key} />;

      })}

    </div>

  );

};



export default AgentArtifacts;


